import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import CopedantEditorPage from "./pages/CopedantEditorPage";
import CopedantImportPage from "./pages/CopedantImportPage";
import CopedantPDFImportPage from "./pages/CopedantPDFImportPage";
import ExplorerPage from "./pages/ExplorerPage";
import UpgradePage from "./pages/UpgradePage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import AccountPage from "./pages/AccountPage";
import ShedTracksPage from "./pages/ShedTracksPage";
import AdminShedTracksPage from "./pages/AdminShedTracksPage";

// Router component to handle session_id detection
const AppRouter = () => {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      {/* Copedant routes */}
      <Route
        path="/copedants/new"
        element={
          <ProtectedRoute>
            <CopedantEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/copedants/import"
        element={
          <ProtectedRoute>
            <CopedantImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/copedants/import-pdf"
        element={
          <ProtectedRoute>
            <CopedantPDFImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/copedants/:id/edit"
        element={
          <ProtectedRoute>
            <CopedantEditorPage />
          </ProtectedRoute>
        }
      />
      {/* Legacy routes - redirect to new paths */}
      <Route path="/copedant/new" element={<Navigate to="/copedants/new" replace />} />
      <Route path="/copedant/:id" element={<Navigate to={`/copedants/${window.location.pathname.split('/').pop()}/edit`} replace />} />
      {/* Explorer - uses query param */}
      <Route
        path="/explorer"
        element={
          <ProtectedRoute>
            <ExplorerPage />
          </ProtectedRoute>
        }
      />
      {/* Legacy explorer route with path param - redirect to query param */}
      <Route
        path="/explorer/:copedantId"
        element={<ExplorerRedirect />}
      />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <UpgradePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute>
            <BillingSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />
      {/* Shed Tracks */}
      <Route
        path="/shed-tracks"
        element={
          <ProtectedRoute>
            <ShedTracksPage />
          </ProtectedRoute>
        }
      />
      {/* Admin Shed Tracks */}
      <Route
        path="/admin/shed-tracks"
        element={
          <ProtectedRoute>
            <AdminShedTracksPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Redirect from /explorer/:copedantId to /explorer?copedantId=:id
const ExplorerRedirect = () => {
  const location = useLocation();
  const copedantId = location.pathname.split('/').pop();
  return <Navigate to={`/explorer?copedantId=${copedantId}`} replace />;
};

// Redirect root based on auth state
const RootRedirect = () => {
  const { user, loading, authError } = useAuth();

  // Only show spinner when actually loading
  if (loading) {
    console.log('[RootRedirect] Loading state, showing spinner');
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00E5FF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // After loading complete, redirect based on user state
  console.log('[RootRedirect] Loading complete, user:', user ? user.email : 'null');
  
  if (authError) {
    console.log('[RootRedirect] Auth error detected, redirecting to login');
  }
  
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App dark">
          <AppRouter />
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
